import { useState, useEffect } from 'react';
import {
  Search, Sparkles, TrendingUp, TrendingDown, AlertTriangle,
  DollarSign, Activity, Brain, ArrowRight, Zap, BarChart3, Loader2
} from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, ZAxis } from 'recharts';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../utils/firebase/client';
import { getCompanyId } from '../../utils/tenantContext';
import { safetyService } from '../../utils/services/safetyService';
import { useTheme } from '../../hooks/useTheme';

interface ManagerInsightsProps {
  companyId?: string;  // Passed from parent for reliable tenant context
}

export default function ManagerInsights({ companyId: propCompanyId }: ManagerInsightsProps) {
  const { isDarkMode } = useTheme();
  const [queryText, setQueryText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);

  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [efficiencyData, setEfficiencyData] = useState<any[]>([]);
  const [insights, setInsights] = useState<any[]>([]);
  const [kpiData, setKpiData] = useState({
    revenue: 0,
    efficiency: 0,
    atRisk: 0
  });

  useEffect(() => {
    loadData();
  }, [propCompanyId]);

  const loadData = async () => {
    // Use prop companyId first (reliable from parent user object), fallback to context
    const companyId = propCompanyId || getCompanyId();
    if (!companyId || companyId === 'default') {
      console.warn('⚠️ ManagerInsights: No valid companyId available');
      setLoading(false);
      return;
    }

    console.log('📊 ManagerInsights loading data for tenant:', companyId);

    try {
      // 1. Load Wells first (to get IDs for production queries)
      const wellsRef = collection(db, `companies/${companyId}/wells`);
      const wellsSnap = await getDocs(wellsRef);
      const wellIds = wellsSnap.docs.map(d => d.id);

      // 2. Load Production Data for Revenue (Last 7 Days)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);

      const dailyRevenue: Record<string, number> = {};
      // Initialize last 7 days with 0
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dateStr = d.toLocaleDateString('en-US', { weekday: 'short' });
        dailyRevenue[dateStr] = 0;
      }

      // Query production for each well (parallel)
      await Promise.all(wellIds.map(async (wellId) => {
        const prodRef = collection(db, `companies/${companyId}/wells/${wellId}/production_entries`);
        const prodQuery = query(
          prodRef,
          where('entryDate', '>=', startDate.toISOString().split('T')[0]),
          orderBy('entryDate', 'asc')
        );

        try {
          const prodSnap = await getDocs(prodQuery);
          prodSnap.docs.forEach(doc => {
            const data = doc.data();
            const date = new Date(data.entryDate).toLocaleDateString('en-US', { weekday: 'short' });
            const oil = data.oilVolume || data.oil_volume_bbl || 0;
            const gas = data.gasVolume || data.gas_volume_mcf || 0;

            const revenue = oil * 70 + gas * 3;
            if (dailyRevenue[date] !== undefined) {
              dailyRevenue[date] += revenue;
            }
          });
        } catch (e) {
        }
      }));

      const chartData = Object.entries(dailyRevenue).map(([date, actual]) => ({
        date,
        actual,
        predicted: 0
      }));

      setRevenueData(chartData);
      const totalRevenue = Object.values(dailyRevenue).reduce((a, b) => a + b, 0);

      // 3. Load Wells for Efficiency
      setEfficiencyData([]);

      // 4. Load Alerts for Insights
      const alerts = await safetyService.getRecentAlerts(5);
      const formattedInsights = alerts.map(alert => ({
        id: alert.id,
        type: alert.severity === 'critical' ? 'critical' : 'trend',
        title: alert.type.replace(/_/g, ' ').toUpperCase(),
        message: alert.details?.description || `Alert type: ${alert.type}`,
        action: 'View Details',
        icon: alert.severity === 'critical' ? AlertTriangle : Activity,
        severity: alert.severity
      }));
      setInsights(formattedInsights);

      setKpiData({
        revenue: totalRevenue,
        efficiency: 0,
        atRisk: alerts.filter(a => a.severity === 'critical').length
      });

    } catch (error) {
      console.error("Failed to load insights:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    setTimeout(() => setIsSearching(false), 1000);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className={`w-8 h-8 animate-spin ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* AI Search Bar */}
      <div className="relative max-w-3xl mx-auto text-center space-y-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${isDarkMode
              ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
              : 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border border-indigo-100'
            }`}
        >
          <Sparkles className="w-3 h-3" />
          <span>Powered by OilWellX Intelligence Engine</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
        >
          Ask your field anything.
        </motion.h1>

        <motion.form
          onSubmit={handleSearch}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="relative"
        >
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full opacity-20 group-hover:opacity-40 transition duration-200 blur"></div>
            <div className={`relative flex items-center rounded-full shadow-xl ${isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white'
              }`}>
              <Search className={`w-5 h-5 ml-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                placeholder="Try 'Show me wells with declining pressure' or 'Predict next month's revenue'..."
                className={`w-full py-4 px-4 bg-transparent border-none focus:ring-0 placeholder-gray-400 ${isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}
              />
              <Button
                type="submit"
                className="mr-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6"
                disabled={!queryText}
              >
                {isSearching ? 'Thinking...' : 'Ask'}
              </Button>
            </div>
          </div>
        </motion.form>
      </div>

      {/* KPI Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Revenue Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, type: "spring" }}
          whileHover={{ scale: 1.05, y: -4 }}
          whileTap={{ scale: 0.98 }}
          className={`
            relative overflow-hidden rounded-3xl
            ${isDarkMode
              ? 'bg-gradient-to-br from-emerald-500 via-emerald-600 to-green-500 p-6'
              : 'bg-gradient-to-br from-emerald-400/20 via-emerald-500/20 to-green-400/20 backdrop-blur p-1 border-3 border-white/60'
            }
          `}
        >
          <div className={`
            ${isDarkMode ? '' : 'bg-white/85 backdrop-blur rounded-[1.3rem] p-6'}
          `}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className={`text-sm mb-2 ${isDarkMode ? 'text-white/90' : 'text-gray-600'}`}>
                  Projected Revenue (7d)
                </p>
                <motion.h3
                  className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                  initial={{ scale: 1.3, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  ${kpiData.revenue.toLocaleString()}
                </motion.h3>
              </div>
              <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                <DollarSign className={`w-8 h-8 ${isDarkMode ? 'text-white' : 'text-emerald-600'}`} strokeWidth={2.5} />
              </motion.div>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className={`w-4 h-4 ${isDarkMode ? 'text-white/80' : 'text-emerald-600'}`} />
              <span className={`text-sm font-medium ${isDarkMode ? 'text-white/80' : 'text-emerald-600'}`}>
                Real-time
              </span>
              <span className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-500'}`}>
                based on production
              </span>
            </div>
          </div>
        </motion.div>

        {/* Efficiency Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, type: "spring" }}
          whileHover={{ scale: 1.05, y: -4 }}
          whileTap={{ scale: 0.98 }}
          className={`
            relative overflow-hidden rounded-3xl
            ${isDarkMode
              ? 'bg-gradient-to-br from-cyan-500 via-cyan-600 to-blue-500 p-6'
              : 'bg-gradient-to-br from-cyan-400/20 via-cyan-500/20 to-blue-400/20 backdrop-blur p-1 border-3 border-white/60'
            }
          `}
        >
          <div className={`
            ${isDarkMode ? '' : 'bg-white/85 backdrop-blur rounded-[1.3rem] p-6'}
          `}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className={`text-sm mb-2 ${isDarkMode ? 'text-white/90' : 'text-gray-600'}`}>
                  Field Efficiency Score
                </p>
                <motion.h3
                  className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                  initial={{ scale: 1.3, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  {kpiData.efficiency || 0}/100
                </motion.h3>
              </div>
              <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                <Activity className={`w-8 h-8 ${isDarkMode ? 'text-white' : 'text-cyan-600'}`} strokeWidth={2.5} />
              </motion.div>
            </div>
            <span className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-500'}`}>
              Calculating...
            </span>
          </div>
        </motion.div>

        {/* At-Risk Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, type: "spring" }}
          whileHover={{ scale: 1.05, y: -4 }}
          whileTap={{ scale: 0.98 }}
          className={`
            relative overflow-hidden rounded-3xl
            ${isDarkMode
              ? 'bg-gradient-to-br from-orange-500 via-orange-600 to-red-500 p-6'
              : 'bg-gradient-to-br from-orange-400/20 via-orange-500/20 to-red-400/20 backdrop-blur p-1 border-3 border-white/60'
            }
          `}
        >
          <div className={`
            ${isDarkMode ? '' : 'bg-white/85 backdrop-blur rounded-[1.3rem] p-6'}
          `}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className={`text-sm mb-2 ${isDarkMode ? 'text-white/90' : 'text-gray-600'}`}>
                  At-Risk Assets
                </p>
                <motion.h3
                  className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                  initial={{ scale: 1.3, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  {kpiData.atRisk} Wells
                </motion.h3>
              </div>
              <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                <AlertTriangle className={`w-8 h-8 ${isDarkMode ? 'text-white' : 'text-orange-600'}`} strokeWidth={2.5} />
              </motion.div>
            </div>
            <div className="flex flex-col gap-1">
              <span className={`text-sm font-medium ${isDarkMode ? 'text-white/80' : 'text-orange-600'}`}>
                Action Required
              </span>
              <span className={`text-xs ${isDarkMode ? 'text-white/70' : 'text-gray-500'}`}>
                Based on critical alerts
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Charts Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Revenue Chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className={`rounded-3xl ${isDarkMode
                ? 'bg-slate-800/50 border border-slate-700'
                : 'bg-white border border-gray-200 shadow-sm'
              }`}
          >
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <BarChart3 className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                Revenue (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] w-full">
                {revenueData.length > 0 && revenueData.some(d => d.actual > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData}>
                      <defs>
                        <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={isDarkMode ? '#818cf8' : '#6366f1'} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={isDarkMode ? '#818cf8' : '#6366f1'} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#334155' : '#f0f0f0'} />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: isDarkMode ? '#94a3b8' : '#6b7280' }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => `$${value}`}
                        tick={{ fill: isDarkMode ? '#94a3b8' : '#6b7280' }}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: '12px',
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                          color: isDarkMode ? '#fff' : '#000'
                        }}
                        formatter={(value: number) => [`$${value.toLocaleString()}`, '']}
                      />
                      <Area
                        type="monotone"
                        dataKey="actual"
                        stroke={isDarkMode ? '#818cf8' : '#6366f1'}
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorActual)"
                        name="Actual Revenue"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className={`flex items-center justify-center h-full ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No revenue data available for this period
                  </div>
                )}
              </div>
            </CardContent>
          </motion.div>

          {/* Efficiency Chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className={`rounded-3xl ${isDarkMode
                ? 'bg-slate-800/50 border border-slate-700'
                : 'bg-white border border-gray-200 shadow-sm'
              }`}
          >
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <Activity className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                Lifting Cost Efficiency Matrix
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] w-full">
                {efficiencyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#f0f0f0'} />
                      <XAxis
                        type="number"
                        dataKey="production"
                        name="Production"
                        unit=" bbl"
                        label={{ value: 'Production (bbl/day)', position: 'bottom', offset: 0, fill: isDarkMode ? '#94a3b8' : '#6b7280' }}
                        tick={{ fill: isDarkMode ? '#94a3b8' : '#6b7280' }}
                      />
                      <YAxis
                        type="number"
                        dataKey="cost"
                        name="Lifting Cost"
                        unit=" $/bbl"
                        label={{ value: 'Cost ($/bbl)', angle: -90, position: 'left', fill: isDarkMode ? '#94a3b8' : '#6b7280' }}
                        tick={{ fill: isDarkMode ? '#94a3b8' : '#6b7280' }}
                      />
                      <Tooltip
                        cursor={{ strokeDasharray: '3 3' }}
                        contentStyle={{
                          borderRadius: '12px',
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                          color: isDarkMode ? '#fff' : '#000'
                        }}
                      />
                      <Scatter name="Wells" data={efficiencyData} fill={isDarkMode ? '#818cf8' : '#8884d8'} />
                    </ScatterChart>
                  </ResponsiveContainer>
                ) : (
                  <div className={`flex items-center justify-center h-full ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No efficiency data available
                  </div>
                )}
              </div>
            </CardContent>
          </motion.div>
        </div>

        {/* AI Insights Feed */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <Brain className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              Intelligence Feed
            </h3>
            <motion.span
              animate={{
                scale: [1, 1.1, 1],
                opacity: [1, 0.8, 1]
              }}
              transition={{
                duration: 2,
                repeat: Infinity
              }}
              className={`text-xs font-medium px-2 py-1 rounded-full ${isDarkMode
                  ? 'bg-indigo-600 text-white'
                  : 'bg-indigo-50 text-indigo-600'
                }`}
            >
              Live
            </motion.span>
          </div>

          <div className="space-y-4">
            {insights.length > 0 ? insights.map((insight, index) => (
              <motion.div
                key={insight.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className={`rounded-2xl p-4 ${isDarkMode
                    ? 'bg-slate-800/50 border border-slate-700'
                    : 'bg-white border border-gray-200 shadow-sm'
                  }`}
              >
                <div className="flex gap-4">
                  <div className={`
                    w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                    ${insight.severity === 'critical'
                      ? isDarkMode
                        ? 'bg-gradient-to-br from-red-500 to-rose-500'
                        : 'bg-gradient-to-br from-red-400/20 to-rose-400/20 border border-red-300'
                      : isDarkMode
                        ? 'bg-gradient-to-br from-blue-500 to-cyan-500'
                        : 'bg-gradient-to-br from-blue-400/20 to-cyan-400/20 border border-blue-300'
                    }
                  `}>
                    <insight.icon className={`w-5 h-5 ${isDarkMode ? 'text-white' :
                        insight.severity === 'critical' ? 'text-red-600' : 'text-blue-600'
                      }`} />
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {insight.title}
                    </h4>
                    <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {insight.message}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`mt-3 p-0 h-auto font-medium text-xs flex items-center gap-1 hover:bg-transparent ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'
                        }`}
                    >
                      {insight.action} <ArrowRight className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )) : (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No active insights
              </div>
            )}
          </div>

          {/* Weekly Report Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8 }}
            whileHover={{ scale: 1.02 }}
            className="relative overflow-hidden rounded-3xl"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700"></div>
            <div className="relative p-6 text-white">
              <h4 className="font-bold text-lg mb-2">Weekly Report Ready</h4>
              <p className="text-indigo-100 text-sm mb-4 leading-relaxed">
                Your AI-generated summary of field performance, financial metrics, and operational risks is ready for review.
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button className="w-full bg-white text-indigo-600 hover:bg-indigo-50 border-none shadow-lg">
                  Download PDF
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
